import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BASE_PRICE_LIST,
  type DuplicateListRequest,
  type EntryResponse,
  italianToday,
  type LookupEntry,
  type PriceListKind,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/** «dal 01/10/2026 al 31/12/2027», or nothing without dates. */
function validityOf(list: LookupEntry): string {
  const from = list.values?.validFrom;
  const to = list.values?.validTo;
  if (typeof from !== 'string' || !from) return '';
  return typeof to === 'string' && to
    ? `dal ${formatDate(from)} al ${formatDate(to)}`
    : `dal ${formatDate(from)}`;
}

/**
 * The lists of a table of voices (docs/analisi/proposta-listini.md): the Base
 * and the lists of the clients, one shown at a time, and «Duplica…».
 */
export function PriceListBar({
  kind,
  lists,
  current,
  canDuplicate,
  onChoose,
}: {
  kind: PriceListKind;
  lists: LookupEntry[];
  current: string;
  canDuplicate: boolean;
  onChoose: (code: string) => void;
}) {
  const [duplicating, setDuplicating] = useState(false);
  const shown = lists.find((list) => list.code === current);
  return (
    <div className="price-lists">
      <div className="row between">
        <div className="row" role="group" aria-label="Listini">
          {lists.map((list) => (
            <button
              key={list.code}
              type="button"
              className={list.code === current ? 'list-chip selected' : 'list-chip'}
              aria-pressed={list.code === current}
              onClick={() => onChoose(list.code)}
            >
              <strong>{list.description}</strong>
              {list.code === BASE_PRICE_LIST[kind] && <span className="pill brand">base</span>}
              {validityOf(list) && <small className="muted">{validityOf(list)}</small>}
            </button>
          ))}
        </div>
        {canDuplicate && shown && (
          <button type="button" className="small" onClick={() => setDuplicating(true)}>
            Duplica «{shown.description}»…
          </button>
        )}
      </div>
      <p className="muted small">
        {current === BASE_PRICE_LIST[kind]
          ? 'Il Base è completo: vale per tutti i clienti senza un listino proprio.'
          : 'Le voci in grigio, «dal Base», non sono in questo listino: vale il Base. «Personalizza» ne crea una propria con un importo diverso.'}
      </p>
      {duplicating && shown && (
        <DuplicateListDialog
          source={shown}
          onClose={() => setDuplicating(false)}
          onDone={(created) => {
            setDuplicating(false);
            onChoose(created.code);
          }}
        />
      )}
    </div>
  );
}

/** Duplica: name, validity, with or without the amounts (decision of 01/10/2026). */
function DuplicateListDialog({
  source,
  onClose,
  onDone,
}: {
  source: LookupEntry;
  onClose: () => void;
  onDone: (created: EntryResponse) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [validFrom, setValidFrom] = useState(italianToday());
  const [validTo, setValidTo] = useState('');
  const [withData, setWithData] = useState(false);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const duplicate = useMutation({
    mutationFn: (body: DuplicateListRequest) =>
      apiRequest<EntryResponse>(
        `/tables/listini/entries/${encodeURIComponent(source.code)}/duplicate`,
        { method: 'POST', body },
      ),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      onDone(created);
    },
  });

  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          duplicate.mutate({
            name,
            validFrom: validFrom || null,
            validTo: validTo || null,
            withData,
          });
        }}
      >
        <h2>Duplica «{source.description}»</h2>
        <Field label="Nome del nuovo listino">
          <input
            value={name}
            maxLength={200}
            required
            autoFocus
            placeholder="Es. Cantieri navali"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="row">
          <Field label="Valido dal">
            <input
              type="date"
              value={validFrom}
              onChange={(event) => setValidFrom(event.target.value)}
            />
          </Field>
          <Field label="Valido fino al (facoltativo)">
            <input
              type="date"
              value={validTo}
              onChange={(event) => setValidTo(event.target.value)}
            />
          </Field>
        </div>
        <fieldset className="choices">
          <legend>Cosa copiare</legend>
          <label className="choice">
            <input type="radio" checked={!withData} onChange={() => setWithData(false)} />
            <span>
              <strong>Senza dati</strong>
              <small className="muted block">
                Le voci senza importi: si compilano solo quelle diverse, le altre restano del Base.
              </small>
            </span>
          </label>
          <label className="choice">
            <input type="radio" checked={withData} onChange={() => setWithData(true)} />
            <span>
              <strong>Con dati</strong>
              <small className="muted block">Le voci con i loro importi.</small>
            </span>
          </label>
        </fieldset>
        {duplicate.error && <p className="error">{duplicate.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={duplicate.isPending || !name.trim()}>
            {duplicate.isPending ? 'Duplicazione…' : 'Duplica'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
